import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import Icone from '../components/Icones';
import Contador from '../components/Contador';
import { corStatus, rotuloStatus } from '../lib/rotulos';

interface Totais {
  total: number;
  abertas: number;
  em_andamento: number;
  aguardando_aprovacao: number;
  concluidas: number;
  interrompidas: number;
  canceladas: number;
  alta_urgente: number;
  horas_trabalhadas_hhmm?: string;
}

interface PorStatus { status: string; qtd: number | string; }
interface Indicadores { totais: Totais; por_status?: PorStatus[]; }

interface OS {
  id: number;
  codigo: string;
  tipo_os: string;
  status: string;
  cliente_nome?: string | null;
  veiculo_placa?: string | null;
  veiculo_marca?: string | null;
  veiculo_modelo?: string | null;
  veiculo_ano?: number | null;
  tecnico_nome?: string | null;
}

function moedaZero(v?: string | number | null): string {
  if (v === null || v === undefined || v === '') return '-';
  return String(v);
}

function veiculoLabel(os: OS): string {
  return [os.veiculo_placa, os.veiculo_marca, os.veiculo_modelo, os.veiculo_ano].filter(Boolean).join(' ');
}

// Com "to", o card vira link para a lista de OS ja filtrada (ex.: Em execucao ->
// /app/os?status=em_andamento).
function Stat({ icone, titulo, valor, cor, detalhe, to }: { icone: string; titulo: string; valor: ReactNode; cor: string; detalhe?: string; to?: string }) {
  const conteudo = (
    <>
      <div className="stat-ic" style={{ background: cor + '1f', color: cor }}><Icone nome={icone} size={24} /></div>
      <div>
        <div className="stat-v" style={{ color: cor }}>{valor}</div>
        <div className="stat-l">{titulo}</div>
        {detalhe && <div style={{ color: 'var(--texto-suave)', fontSize: 12 }}>{detalhe}</div>}
      </div>
    </>
  );
  if (!to) return <div className="stat">{conteudo}</div>;
  return (
    <Link to={to} className="stat" title={`Ver as OS: ${titulo}`} style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
      {conteudo}
    </Link>
  );
}

function BarrasStatus({ fatias }: { fatias: PorStatus[] }) {
  const normalizadas = fatias.map((f) => ({ ...f, qtd: Number(f.qtd) }));
  const max = Math.max(1, ...normalizadas.map((f) => f.qtd));
  return (
    <div className="card">
      <h3>OS por status</h3>
      <div className="barras">
        {normalizadas.map((f) => (
          <div className="barra-item" key={f.status}>
            <span className="barra-lbl">{rotuloStatus(f.status)}</span>
            <span className="barra-track"><span className="barra-fill" style={{ width: (f.qtd / max) * 100 + '%', background: corStatus(f.status) }} /></span>
            <span className="barra-qtd">{f.qtd}</span>
          </div>
        ))}
        {!normalizadas.length && <p className="vazio">Sem dados ainda.</p>}
      </div>
    </div>
  );
}

export default function DashboardOficina() {
  const [indicadores, setIndicadores] = useState<Indicadores | null>(null);
  const [ordens, setOrdens] = useState<OS[]>([]);
  const [erro, setErro] = useState('');

  useEffect(() => {
    Promise.all([
      api<Indicadores>('/dashboard/indicadores.php'),
      api<OS[]>('/os/os_listar.php'),
    ])
      .then(([ind, os]) => {
        setIndicadores(ind);
        setOrdens(os.slice(0, 6));
      })
      .catch((e) => setErro(e instanceof Error ? e.message : String(e)));
  }, []);

  return (
    <div>
      <div className="titulo-pg">
        <div>
          <h1>Dashboard da oficina</h1>
          <p className="muted">Visão das ordens de serviço geradas para veículos.</p>
        </div>
        <div className="acoes">
          <Link to="/app/orcamentos" className="btn">Criar chamado</Link>
          <Link to="/app/agenda" className="btn sec">Agenda</Link>
          <Link to="/app/os" className="btn sec">Ver OS</Link>
        </div>
      </div>

      {erro && <div className="msg-erro">{erro}</div>}

      {indicadores && (
        <>
          <div className="grid cols4">
            <Stat icone="prancheta" titulo="OS registradas" to="/app/os" cor="#2563eb" valor={<Contador alvo={indicadores.totais.total} />} detalhe="total da oficina" />
            <Stat icone="chave" titulo="Abertas" to="/app/os?status=aberto" cor="#0a49c2" valor={<Contador alvo={indicadores.totais.abertas} />} detalhe="aguardando execução" />
            <Stat icone="engrenagem" titulo="Em execução" to="/app/os?status=em_andamento" cor="#d97706" valor={<Contador alvo={indicadores.totais.em_andamento} />} detalhe="serviço em andamento" />
            <Stat icone="checklist" titulo="Concluídas" to="/app/os?status=concluido" cor="#16a34a" valor={<Contador alvo={indicadores.totais.concluidas} />} detalhe="serviços finalizados" />
          </div>

          <div className="grid cols4" style={{ marginTop: 16 }}>
            <Stat icone="calendario" titulo="Aguardando cliente" to="/app/os?status=aguardando_aprovacao" cor="#7c3aed" valor={<Contador alvo={indicadores.totais.aguardando_aprovacao} />} detalhe="aprovação pendente" />
            <Stat icone="raio" titulo="Alta / urgente" to="/app/os?prioridade=alta,urgente" cor="#dc2626" valor={<Contador alvo={indicadores.totais.alta_urgente} />} detalhe="prioridade crítica" />
            <Stat icone="relogio" titulo="Horas trabalhadas" cor="#0f766e" valor={moedaZero(indicadores.totais.horas_trabalhadas_hhmm)} detalhe="tempo das OS" />
            <Stat icone="caixa" titulo="Canceladas" to="/app/os?status=cancelado" cor="#64748b" valor={<Contador alvo={indicadores.totais.canceladas} />} detalhe={`${indicadores.totais.interrompidas} interrompidas`} />
          </div>

          <div className="grid cols2" style={{ marginTop: 18 }}>
            <BarrasStatus fatias={indicadores.por_status || []} />
            <div className="card">
              <h3>Últimas ordens de serviço</h3>
              <div className="tabela-wrap" style={{ border: 0, boxShadow: 'none' }}>
                <table style={{ minWidth: 0 }}>
                  <thead><tr><th>OS</th><th>Veículo</th><th>Cliente</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {ordens.map((os) => (
                      <tr key={os.id}>
                        <td><strong>{os.codigo}</strong><div className="muted">{os.tipo_os}</div></td>
                        <td>{veiculoLabel(os) || '-'}</td>
                        <td>{os.cliente_nome || '-'}</td>
                        <td><span className="badge" style={{ background: 'transparent', color: corStatus(os.status), border: `1px solid ${corStatus(os.status)}` }}>{rotuloStatus(os.status)}</span></td>
                        <td style={{ textAlign: 'right' }}><Link to={'/app/os/' + os.id} className="btn sec sm">Abrir</Link></td>
                      </tr>
                    ))}
                    {!ordens.length && <tr><td colSpan={5} className="vazio">Nenhuma OS encontrada.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
